import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles4() {
  const { lesson, course, accent } = findLesson("the-beatles", "4");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The ghost note" title="An extra soft hit on a weak beat">
        <Prose>
          <p>
            Part 2 of "With a Little Help from My Friends" focuses on how to make a solo piano
            arrangement feel alive — not just correct. The first technique Jason introduces is the
            <strong> ghost note</strong>: a very soft extra note added to the left-hand pattern
            on a weak beat.
          </p>
          <p>
            In the left hand, after the main chord hits, you add one more note quietly — usually
            another note of the same chord. On E major, Jason's ghost is the B: "Adding this
            extra, this B would be the ghost." The ghost note is not a structural hit — it's a
            rhythmic connector. It keeps the groove moving without calling attention to itself.
          </p>
          <p>
            The key to making it work is volume. "The ghost note could perhaps be played a lot
            softer. It's generally just a — it's not an important — I mean it is important for the
            groove, but it shouldn't be too loud. It should be softer in comparison with the other
            points because it's at a weak beat."
          </p>
        </Prose>

        <TheoryBox label="Ghost note — how it works">
          A ghost note is placed on an off-beat or weak beat, played very softly. It is never the
          main hit of the bar. You can ghost with any remaining note of the current chord — on a
          B7, Jason even suggests ghosting the seventh for variety. The softer the ghost, the more
          groove it adds without cluttering the texture.
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="0C58ttB2-Qg"
        title="With a Little Help from My Friends — The Beatles"
        caption="Listen to the groove of the band. Part 2 recreates it on solo piano through ghost notes, off-beat emphasis, and the blues lick at the five chord (B major). All these techniques apply to solo piano playing."
      />

      <Chapter eyebrow="Three zones of the left hand" title="Low, middle, and upper — each has a different job">
        <Prose>
          <p>
            One of Jason's clearest teaching points in this class is the division of the piano
            keyboard into three functional zones for the left hand:
          </p>
          <p>
            The <strong>low zone</strong> (well below middle C) is for sound effects, root notes,
            and groove. "Here you need to probably think like a drummer or a bass player or groove
            in general." In a band environment, you can even play lower
            than the bassist. In this zone, you play the roots of chords and create rhythmic
            momentum. You don't play inversions or voice-leading here — "here you don't really
            use inversions in this area."
          </p>
          <p>
            The <strong>middle zone</strong> (around and slightly below middle C) is where voicing
            and voice-leading happen. "If you are an octave around middle C, you have to focus on
            voice leading, how the thing moves to the next chord, you know, inversions as we also
            know it by." This is where you can
            hear the individual voices of the chord clearly and control how each note moves to
            the next chord.
          </p>
          <p>
            The <strong>upper zone</strong> (above middle C, approaching the right hand range) is
            melody territory. "You should think that this is also your right hand. Like what is my
            right hand doing? Okay, if my left hand were to do it, great. I'll do the same thing."
            Here you can add
            melodic counterpoint or complete the right hand's work.
          </p>
        </Prose>

        <MTQuote>
          "If you do this here, there's no value. So what you want to do here in this area is to
          groove. Maybe make a riff."
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Open and closed voicing" title="Within or beyond the octave">
        <Prose>
          <p>
            Jason presents two left-hand voicing approaches, and the choice between them changes
            the texture and weight of the sound. <strong>Closed voicing</strong> keeps all chord
            tones within an octave — you play the notes of the chord as close together as possible.
            In Jason's words, "close position would be within octave." <strong>Open voicing</strong>
            spreads the notes beyond an octave, with gaps between some of the voices: "Open position
            would be beyond octave."
          </p>
          <p>
            For the stride and bounce pattern in "With a Little Help," closed voicing gives a
            tighter, more traditional sound. Open voicing gives more resonance and depth — especially
            effective when you take the chord further down the keyboard. "You can get the same
            effect by taking the piano much more deeper."
          </p>
          <p>
            Jason's open voicings put the root low and spread the rest above it. For B major that
            is 1-5-3: B, F♯, then D♯ above the octave. For B7, swap the fifth for the flat seventh:
            1-♭7-3, which is B, A, D♯. The open spread gives the chord more air. You can then add
            the ghost note with whichever remaining note of the chord is closest to hand.
          </p>
        </Prose>

        <TheoryBox label="Stride technique and Scott Joplin">
          Stride piano means the left hand alternates between a low bass note (on beats 1 and 3)
          and a chord in the middle register (on beats 2 and 4). It physically requires the
          hand to "stride" across the keyboard. It grew out of ragtime, and Scott Joplin's rags —
          The Entertainer, Maple Leaf Rag — are built on this kind of left hand. Jason calls
          Joplin "the Chopin of the contemporary piano": a classically minded musician at the
          point where classical music was moving towards jazz and blues. For the wide reaches,
          Jason's tip is to shape the wrist like a U or a boat, with the index finger as an
          anchor that doesn't leave the key while the hand turns.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The blues lick" title="Usually at the five chord">
        <Prose>
          <p>
            For melodic ornamentation in the right hand, Jason introduces the blues lick — and
            gives a rule of thumb for when to use it: "When do I sneak the blues lick in? It's
            always — it's usually at the five chord." In the key of E major ("With a Little Help" is in E major),
            the five chord is B major. That is where the blues lick fits naturally.
          </p>
          <p>
            He tests the rule directly: "See, it doesn't work at an F sharp minor chord." The blues
            lick leans on blue notes — the flattened third, fifth or seventh — and these sound most
            convincing over the five chord. On other chords the lick can sometimes work, but at the
            five, as Jason puts it, you have a better licence to do it.
          </p>
          <p>
            The slide between the second and third degree is the key move: "the 2 to the 3
            slide." In E major around the B chord, you slide up to the chord tone with a
            slight blues inflection. You can even do a kind of answer from the left hand — the
            left hand responds to whatever the right hand plays with a brief counter-gesture.
          </p>
        </Prose>

        <MTQuote>
          "The old favourite for all solo piano renditions has to always be thirds and sixth
          intervals while you play your melodies."
        </MTQuote>
        <Prose>
          <p>Fourths, he adds, can sometimes work too.</p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Backbeat and the Irish rhythm" title="Where the emphasis lands">
        <Prose>
          <p>
            Beyond the ghost note and blues lick, Jason teaches two more rhythmic concepts for
            making the melody feel groovy rather than academic.
          </p>
          <p>
            The first is <strong>off-beat emphasis</strong>: playing the melody notes that fall on
            the "ands" slightly louder than the on-beats. Jason calls this the backbeat — "play the
            backbeat, the and, a bit louder. You're always thinking of the backbeat." (In drumming,
            "backbeat" usually means beats 2 and 4; here Jason means the off-beat "ands".) This is
            what separates a mechanical performance from a groovy one: play someone the version
            without the emphasis and the version with it, and they will hear the difference.
          </p>
          <p>
            The second is what Jason calls the <strong>Irish rhythm</strong>: a triplet feel
            derived from the bodhrán, the Irish frame drum. "Dunk, kata, kata, dunk, kata, kata,
            dunk, kata. It's a bodhrán drum beat." The bodhrán is played with a small double-ended
            beater (the tipper), whose two ends give two strikes for the price of one — which
            naturally produces triplets. Applied to
            piano, this rhythm creates a swung arpeggio feel — something between a straight groove
            and full swing. "Sort of like an Irish groove." This works especially well for the
            faster chord changes in the chorus.
          </p>
          <p>
            Finally: the elbow. "You don't want to move the elbow out, keep it touching the stomach
            and wave it like this." Keeping the elbow close to the body and rotating the forearm rather than
            pushing the elbow outward gives maximum keyboard coverage with minimum tension.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/04-with-a-little-help.png"
          caption="Jason's chart of Joe Cocker's version of 'With a Little Help', in A: the intro riff over G/A → D/A → A, with each chord lasting six eighth notes, then the verse, chorus and bridge chords."
        />
      </Chapter>
    </LessonShell>
  );
}
